import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Button, { ButtonTypes } from 'components/common/button';
import DeleteTeamModal from 'components/modals/delete_team_modal';
import {
  DescriptionTextArea,
  EditTeamPage,
  ErrorMessage,
  InputField,
  InputFieldWrapper,
  PendingSpinnerWrapper,
  TeamCreator,
  TeamEditorFooter,
  TeamEditorLabel,
  TeamEditorSaveButton,
  TeamInfo,
  TeamForm,
} from 'components/admin/users/team_editor/team_editor_components';
import EmptyTeam from 'components/admin/users/team_editor/empty_team';
import { H3 } from 'components/common/headers';
import Err from 'models/err';
import COLORS, { GRAYS } from 'components/common/colors';
import LoadingSpinner from 'components/lib/loading_spinner';
import PageLayout from 'components/page_layout';
import SelectableUsersTable from 'components/admin/users/team_editor/selectable_users_table';
import SelectedUsers from './selected_users';
import Team from 'models/team';
import Tooltip from 'components/common/tooltip';
import WindowSizeWatcher from 'components/common/utilities/window_size_watcher';

export default class TeamEditor extends React.Component {
  constructor(props) {
    super(props);
    _.bindAll(this, [
      'handleCancelDelete',
      'handleConfirmDelete',
      'handleNameChange',
      'handleDescriptionChange',
      'onSubmit',
      'onUserSelectClick',
      'openDeleteTeamModal',
      'removeSelectedUser',
    ]);
    this.state = {
      name: !props.isLoading && props.team.name,
      description: !props.isLoading && props.team.description,
      errors: [],
      selectedUserIds: !props.isLoading ? props.team.agentIds : [],
      _version: !props.isLoading && props.team._version,
      hasModifiedName: false,
      showDeleteModal: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isLoading !== this.props.isLoading) {
      this.setState({
        name: nextProps.team.name,
        description: nextProps.team.description,
        selectedUserIds: nextProps.team.agentIds || [],
        _version: nextProps.team._version,
      });
    }
    this.setState({ errors: nextProps.errors });
  }

  handleNameChange(evt) {
    let newState = {
      errors: this.rejectError('name'),
      hasModifiedName: true,
      name: evt.target.value,
    };

    this.setState(newState);
  }

  rejectError(attr) {
    return this.state.errors.filter(e => e.attr !== attr);
  }

  handleDescriptionChange(evt) {
    this.setState({ description: evt.target.value });
  }

  onUserSelectClick({ userId }) {
    if (_.includes(this.state.selectedUserIds, userId)) {
      const selectedUserIds = this.state.selectedUserIds.filter(id => userId !== id);
      this.setState({ selectedUserIds });
    } else {
      this.setState({ selectedUserIds: this.state.selectedUserIds.concat(userId) });
    }
  }

  removeSelectedUser(userId) {
    const selectedUserIds = this.state.selectedUserIds.filter(id => userId !== id);
    this.setState({ selectedUserIds });
  }

  onSubmit() {
    if (this.props.isPendingSave) {
      return;
    }

    let { name, description, selectedUserIds, _version } = this.state;
    this.setState({ hasModifiedName: false });
    this.props.onSubmit({
      id: this.props.team.id,
      _version,
      name,
      description,
      agentIds: selectedUserIds,
    });
  }

  hasNameError() {
    return !this.state.hasModifiedName && _.find(this.state.errors, error => error.attr === 'name');
  }

  openDeleteTeamModal() {
    if (this.props.isPendingSave) {
      return;
    }
    this.setState({ showDeleteModal: true });
  }

  handleCancelDelete() {
    this.setState({ showDeleteModal: false });
  }

  handleConfirmDelete() {
    this.setState({ showDeleteModal: false });
    this.props.onDelete(this.props.team.id);
  }

  render() {
    if (this.props.isLoading) {
      return (
        <TeamPage>
          <LoadingSpinner color={GRAYS.xlight} data-aid="teamEditor-loadingSpinner" />
        </TeamPage>
      );
    }

    return (
      <TeamPage>
        <EditTeamPage data-aid="teamEditor">
          <SelectableUsersTable
            isNewTeam={this.props.team.isNew()}
            isPendingDelete={this.props.isPendingDelete}
            isPendingSave={this.props.isPendingSave}
            onUserSelectClick={this.onUserSelectClick}
            openDeleteTeamModal={this.openDeleteTeamModal}
            selectedUserIds={this.state.selectedUserIds}
          />
          <TeamInfo>
            <TeamForm inset="large">
              <H3>Team info</H3>
              <InputFieldWrapper>
                <TeamEditorLabel>Name</TeamEditorLabel>
                <InputField
                  data-aid="teamEditorName"
                  className={classnames({ 'teamEditor-inputField-error': this.hasNameError() })}
                  value={this.state.name || ''}
                  onChange={this.handleNameChange}
                />
              </InputFieldWrapper>
              {this.props.createdBy ? (
                <InputFieldWrapper>
                  <TeamEditorLabel>Creator</TeamEditorLabel>
                  <TeamCreator data-aid="teamCreatedBy">{this.props.createdBy}</TeamCreator>
                </InputFieldWrapper>
              ) : null}
              <InputFieldWrapper>
                <TeamEditorLabel>Purpose</TeamEditorLabel>
                <DescriptionTextArea
                  data-aid="teamEditorDescription"
                  value={this.state.description || ''}
                  onChange={this.handleDescriptionChange}
                />
              </InputFieldWrapper>
              {this.state.selectedUserIds.length < 1 ? (
                <EmptyTeam />
              ) : (
                <SelectedUsers userIds={this.state.selectedUserIds} onDeleteItem={this.removeSelectedUser} />
              )}
            </TeamForm>
            <TeamEditorFooter inset="large">
              {this.state.errors.length > 0 ? this.renderErrors() : null}
              <Button
                buttonType={ButtonTypes.TEXT}
                data-aid="teamEditorCancel"
                onClick={this.props.onCancel}
                disabled={this.props.isPendingDelete || this.props.isPendingSave}
              >
                Cancel
              </Button>
              {this.renderSaveButton()}
            </TeamEditorFooter>
          </TeamInfo>
        </EditTeamPage>
        {this.state.showDeleteModal ? (
          <DeleteTeamModal onCancel={this.handleCancelDelete} onConfirm={this.handleConfirmDelete} />
        ) : null}
      </TeamPage>
    );
  }

  isOutdated() {
    return this.props.team._version > this.state._version;
  }

  renderErrors() {
    let errorMessage = this.state.errors[0];
    if (this.state.hasModifiedName) {
      errorMessage = _.find(this.state.errors, err => err.attr !== 'name');
    }
    if (!errorMessage) {
      return null;
    }
    return <ErrorMessage data-aid="teamEditor-errorMessage">{_.upperFirst(errorMessage.detail)}</ErrorMessage>;
  }

  renderSaveButton() {
    const saveButton = (
      <TeamEditorSaveButton
        buttonType={ButtonTypes.PRIMARY}
        data-aid="teamEditorSave"
        onClick={this.onSubmit}
        disabled={this.props.isPendingDelete || this.isOutdated()}
      >
        {this.props.isPendingSave ? (
          <PendingSpinnerWrapper className="teamSaveSpinner">
            <LoadingSpinner containerClassName="teamSaveSpinner" position="relative" size="16px" color={COLORS.white} />
          </PendingSpinnerWrapper>
        ) : (
          'Save'
        )}
      </TeamEditorSaveButton>
    );

    if (!this.isOutdated()) {
      return saveButton;
    }

    // Wrap in div to ensure correct inline positioning of the button because Tooltip is a span
    return (
      <div data-aid="teamEditorDisabledSave">
        <WindowSizeWatcher data-aid="teamEditorDisabledSaveWatcher">
          {({ windowWidth }) => (
            <Tooltip
              className="teamEditor-saveTooltip"
              delay={250}
              message="The team was changed by someone else"
              position="top"
              bounds={{ right: windowWidth }}
            >
              {saveButton}
            </Tooltip>
          )}
        </WindowSizeWatcher>
      </div>
    );
  }
}

const TeamPage = styled(PageLayout)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  & .page-childrenContainer {
    height: 100%;
  }
`;

TeamEditor.propTypes = {
  createdBy: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)).isRequired,
  isLoading: PropTypes.bool,
  isPendingDelete: PropTypes.bool,
  isPendingSave: PropTypes.bool,
  team: PropTypes.instanceOf(Team).isRequired,

  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/users/team_editor/team_editor.jsx